import 'babel-polyfill';
import React, { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { getElementByIdAsync } from './async';
import { language } from './environments/common';
import RouteList from './Utils/Routes';
import RouteAuth from './Utils/RouteAuth';
import { RouterChange, dispatchCustomEvent } from './Utils/Telemetry';
import globalStore from './Stores/GlobalStore';
import { APPLICATION_PROFILE, constants, launcherConstants } from './Constants/Constants';
import { NetService } from 'minecraft-net-core-services';
import 'react-toastify/dist/ReactToastify.css';
import './apps.scss';
import { preloadToastImages } from './Utils/Utils';
import { IntlProvider } from 'react-intl';
import en from './compiled-lang/en-us.json';
import { environment } from './environment';
import { useRouteConfig } from './hooks/useRouteConfig';
import clsx from 'clsx';

const baseName = '/' + language;

const MyAccountSettings = React.lazy(() => import('./Components/AppComponents/MyAccountSettings/MyAccountSettings'));
const AllGamesLanding = React.lazy(() => import('./Components/AppComponents/AllGamesLanding/AllGamesLanding'));
const EditSkin = React.lazy(() => import('./Components/AppComponents/EditSkin/EditSkin'));
const EditProfile = React.lazy(() => import('./Components/AppComponents/EditProfile/EditProfile'));
const Login = React.lazy(() => import('./Pages/Login/Login'));
const MsaProfileLanding = React.lazy(() => import('./Components/AppComponents/MsaProfileLanding/MsaProfileLanding'));
const Redeem = React.lazy(() => import('./Components/AppComponents/Redeem/Redeem'));
const Wallet = React.lazy(() => import('./Components/AppComponents/Wallet/Wallet'));
const OneStoreWallet = React.lazy(() => import('./Components/AppComponents/OneStoreWallet/OneStoreWallet'));
const OneStoreRedeem = React.lazy(() => import('./Components/AppComponents/OneStoreRedeem/OneStoreRedeem'));
const EditRealmJava = React.lazy(() => import('./Components/AppComponents/Realms/Java/EditRealmJava'));
const EditRealmBedrock = React.lazy(() => import('./Components/AppComponents/Realms/Bedrock/EditRealmBedrock'));

const englishLocale = 'en-us';

export const App = () => {
  const location = useLocation();
  const routeConfig = useRouteConfig();
  const [langData, setLangData] = useState<any>({});
  useEffect(() => {
    try {
      NetService.loadActiveExperimentResources({
        application: APPLICATION_PROFILE,
        baseUrl: environment['endpoint.net.minecraftServices'],
        configBaseUrl: window.location.origin,
        property: 'https://www.minecraft.net'
      });
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => {
    const pageName = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);
    const routerChangeEvt = RouterChange(pageName);
    dispatchCustomEvent(routerChangeEvt);
    window.scrollTo(0, 0);
    globalStore.clearError();
  }, [location]);
  useEffect(() => {
    if (window.location.href.split('?')[0].includes(constants.msaprofile)) {
      globalStore.getProfileDetails();
    }
    if (window.location.href.indexOf(launcherConstants.refLauncherParam) > -1) {
      sessionStorage.setItem(launcherConstants.launcherParam, 'Yes');
    }
    preloadToastImages();
  }, []);

  useEffect(() => {
    const loadLangData = async (newLocale: string) => {
      let localeData = {};
      if (newLocale == englishLocale) {
        localeData = en;
      } else {
        // Since we default to english, only fetch non-english data
        localeData = await import(`./compiled-lang/${newLocale}.json`);
      }
      setLangData({
        ...langData,
        [newLocale]: localeData
      });
    };
    loadLangData(language);
  }, [language]);

  return (
    <div className={clsx('site-body--core-apps site-body--my-minecraft', `app__back-ground-color`, `app__back-ground-color-${routeConfig?.cssRouteConfig.pageBackgroundColor}`)}>
      <React.Suspense
        fallback={
          <div className="area-overlay">
            <div className="bg-wool-light" />
            <div className="preloader">
              <div className="bigsquare">
                <div className="square first" />
                <div className="square second" />
                <div className="square third" />
                <div className="square fourth" />
              </div>
              <p>...</p>
            </div>
          </div>
        }>
        <IntlProvider
          locale={language}
          defaultLocale={englishLocale}
          messages={langData[language]}
          defaultRichTextElements={{
            ul: (...chunks) => <ul>{chunks}</ul>,
            li: (...chunks) => <li>{chunks}</li>,
            p: (...chunks) => <p>{chunks}</p>,
            b: (...chunks) => <b>{chunks}</b>,
            h2: (...chunks) => <h2>{chunks}</h2>,
            h3: (...chunks) => <h3>{chunks}</h3>,
            br: () => <br />
          }}>
          <Routes>
            <Route path={RouteList.login.path} element={<Login />} />
            <Route element={<RouteAuth />}>
              <Route path={RouteList.msaProfile.path} element={<MsaProfileLanding />} />
              <Route path={RouteList.msaProfile.path} element={<MsaProfileLanding />}>
                <Route path="" element={<AllGamesLanding />} />
                <Route path={RouteList.myGames.path} element={<AllGamesLanding />} />
                <Route path={RouteList.editProfile.path} element={<EditProfile />} />
                <Route path={RouteList.editSkin.path} element={<EditSkin />} />
                <Route path={RouteList.accountSettings.path} element={<MyAccountSettings />} />
                <Route path={RouteList.redeem.path} element={<Redeem />} />
                <Route path={RouteList.wallet.path} element={<Wallet />} />
                <Route path={RouteList.oneStoreWallet.path} element={<OneStoreWallet />} />
                <Route path={RouteList.oneStoreRedeem.path} element={<OneStoreRedeem />} />
                <Route path={RouteList.editRealmJava.path} element={<EditRealmJava />} />
                <Route path={RouteList.editRealmBedrock.path} element={<EditRealmBedrock />} />
              </Route>
            </Route>
          </Routes>
        </IntlProvider>
      </React.Suspense>
    </div>
  );
};

getElementByIdAsync('CoreAppsApp')
  .then((root) => {
    ReactDOM.render(
      <Router basename={baseName}>
        <App />
      </Router>,
      root
    );
  })
  .catch(console.error);
